import { useMemo, useRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRtfEditor } from "./hooks/useRtfEditor";
import { useRtfHandlers } from "./hooks/useRtfHandlers";
import { useTypingEmitter } from "./hooks/useTypingEmitter";
import { useRecordingGesture } from "./hooks/useRecordingGesture";
import { useRtfMessageLimits } from "./hooks/useRtfMessageLimits";
import RtfInputHeader from "./components/RtfInputHeader";
import RtfInputMain from "./components/RtfInputMain";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useRtfState } from "./hooks/useRtfState";
import { useVoiceRecorder } from "@/lib/voice";
import { VoiceRecordingBar, LockIndicator } from "./VoiceRecordingBar";
import { useRtfInputForChat } from "@/hooks/useRtfInputForChat";
import { useNavigationState } from "@/zustand/NavigationState";
import { Button } from "@/components/primitives/button";
import { CircleCheck, SendHorizonal } from "lucide-react";
import MobileAttachmentMenu from "./attachment/menu/MobileAttachmentMenu";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { cn } from "@/lib/utils";
import DesktopAttachmentMenu from "./attachment/menu/DesktopAttachmentMenu";
import RecordVoiceMessageButton from "./RecordVoiceMessageButton";
import { postNewVoiceMessageToChat } from "@/middleware/messages";
import { generateUuid7 } from "@/utils/uuid7";
import EmojiButton from "./EmojiButton";
import MarkdownToolbar from "./MarkdownToolbar";
import MobileFormattingBubble from "./MobileFormattingBubble";
import PlanLimitUpsell from "@/components/shared/PlanLimitUpsell";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { useSubscriptionState } from "@/zustand/SubscriptionState";

/**
 * Message input component that handles message composition and sending.
 * Uses a textarea with markdown support instead of a rich text editor.
 */
export default function RtfInput(): JSX.Element {
  const newFileAttachment = useFileMessageSenderState().newFileAttachment;
  const newMediaAttachmentUrl =
    useMediaMessageSenderState().newMediaAttachmentUrl;

  const handleSendMessageRef = useRef<(() => void) | null>(null);
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const { editMessage } = useRtfInputForChat(activeChatId);

  // Centralized typing emit + lifecycle. Single owner of the typing timer so
  // keystroke (editor), accepted-send (handlers), idle/clear, chat-switch,
  // unmount, tab-visibility and socket reconnect can't race.
  const { notifyTyping, stopTyping } = useTypingEmitter(activeChatId);

  // Initialize the textarea input
  const {
    textareaRef,
    value,
    updateValue,
    handleChange,
    handleKeyDown,
    handlePaste,
    clearContent,
    setContent,
    focus,
    insertText,
    isEmpty,
  } = useRtfEditor(() => {
    if (handleSendMessageRef.current) {
      handleSendMessageRef.current();
    }
  }, notifyTyping);

  // Initialize handlers
  const { handleSendMessage } = useRtfHandlers({
    value,
    clearContent,
    focus,
    stopTyping,
  });

  handleSendMessageRef.current = handleSendMessage;

  const { replyMessage, resetRtfInputState } = useRtfInputForChat(activeChatId);

  // Sync state (drafts, edit mode, reply mode)
  useRtfState({ setContent, focus, isEmpty });

  // Compute live message limit state
  const messageLimits = useRtfMessageLimits(value);

  const canSendMessage =
    messageLimits.charCount > 0 || newFileAttachment || newMediaAttachmentUrl;

  const sendDisabled = useMemo(() => {
    if (messageLimits.isOverCharLimit || messageLimits.isOverEncryptedLimit) {
      return true;
    }
    if (newFileAttachment || newMediaAttachmentUrl) {
      return false;
    }
    return messageLimits.charCount === 0;
  }, [
    messageLimits.isOverCharLimit,
    messageLimits.isOverEncryptedLimit,
    messageLimits.charCount,
    newFileAttachment,
    newMediaAttachmentUrl,
  ]);

  const { t } = useTranslation();
  const { voiceNoteDuration } = usePlanLimits();
  const plan = useSubscriptionState((s) => s.subscription.plan);

  const {
    record,
    stop,
    cancel,
    requestMic,
    voiceRecordingStatus,
    duration,
    isNearMaxDuration,
    stoppedAtLimit,
    liveAmplitude,
  } = useVoiceRecorder(voiceNoteDuration);

  // Show plan upsell banner after recording auto-stops at the plan limit
  const [showVoiceUpsell, setShowVoiceUpsell] = useState(false);

  const isRecording = voiceRecordingStatus === "recording";

  const replyAtRecordStartRef = useRef(replyMessage);
  useEffect(() => {
    if (isRecording) {
      replyAtRecordStartRef.current = replyMessage;
      setShowVoiceUpsell(false); // clear banner when a new recording starts
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  const handleStopAndSend = useCallback(async () => {
    if (!activeChatId) {
      return;
    }

    let result: { file: File; duration: number; waveform: number[] };
    try {
      result = await stop();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("stopRecording failed:", err);
      return;
    }

    const hasParentMessage = replyAtRecordStartRef.current;
    resetRtfInputState();

    const newAttachmentId = generateUuid7();
    await postNewVoiceMessageToChat({
      newAttachmentId,
      chatId: activeChatId,
      file: result.file,
      parentMessage: hasParentMessage,
      duration: result.duration,
      waveform: result.waveform,
    });
  }, [activeChatId, stop, resetRtfInputState]);

  const {
    slideProgress,
    lockProgress,
    isLocked,
    onPointerDown: gesturePointerDown,
    reset: resetGesture,
    startLocked,
  } = useRecordingGesture({
    onSlideCancel: cancel,
    onLock: () => {},
    onRelease: () => {
      void handleStopAndSend();
    },
    enabled: isRecording,
  });

  const handleStartRecording = useCallback(async () => {
    await record();
  }, [record]);

  const handleGestureStart = useCallback(
    (origin: { x: number; y: number }) => {
      gesturePointerDown(origin);
    },
    [gesturePointerDown],
  );

  const handleLockedSend = useCallback(async () => {
    await handleStopAndSend();
    resetGesture();
  }, [resetGesture, handleStopAndSend]);

  const handleLockedCancel = useCallback(() => {
    resetGesture();
    cancel();
  }, [resetGesture, cancel]);

  // Recording started but user released before gesture tracking began (race condition, touch only)
  const handleImmediateSend = useCallback(() => {
    void handleStopAndSend();
  }, [handleStopAndSend]);

  // Non-touch: recording started via single click — enter lock mode immediately
  const handleImmediateLock = useCallback(() => {
    startLocked();
  }, [startLocked]);

  // ── Max-duration auto-stop ──
  // When useVoiceRecorder hits the plan duration limit it internally stops the
  // recorder (both hold and locked mode). stoppedAtLimit is set synchronously
  // in the hook before endRecordingSession() fires, so it's reliable here.
  // isAtMaxDuration alone is not sufficient — it gets reset before this effect runs.
  const prevRecordingRef = useRef(isRecording);
  useEffect(() => {
    const wasRecording = prevRecordingRef.current;
    prevRecordingRef.current = isRecording;

    if (wasRecording && !isRecording && stoppedAtLimit) {
      if (plan !== "premium") {
        setShowVoiceUpsell(true);
      }
      resetGesture();
      void handleStopAndSend();
    }
  }, [isRecording, stoppedAtLimit, plan, resetGesture, handleStopAndSend]);

  const nextPlan =
    plan === "free"
      ? "standard"
      : plan === "standard"
        ? "pro"
        : plan === "pro"
          ? "proplus"
          : "premium";

  return (
    <div className="flex select-none flex-col gap-1">
      <RtfInputHeader />
      {showVoiceUpsell && (
        <PlanLimitUpsell
          message={t("subscription.voiceNoteLimitReached")}
          targetPlan={nextPlan}
        />
      )}
      {isRecording ? (
        <div className="flex flex-col gap-2">
          <VoiceRecordingBar
            duration={duration}
            isNearMaxDuration={isNearMaxDuration}
            liveAmplitude={liveAmplitude}
            isLocked={isLocked}
            slideProgress={slideProgress}
            onSend={handleLockedSend}
            onCancel={handleLockedCancel}
          />
          {!isLocked && (
            <div className="flex justify-end">
              <LockIndicator lockProgress={lockProgress} />
            </div>
          )}
        </div>
      ) : (
        <RtfInputMain
          textareaRef={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          charCount={messageLimits.charCount}
          charLimit={messageLimits.charLimit}
          approxRemainingChars={messageLimits.approxRemainingChars}
          showCloseToLimitWarning={messageLimits.showCloseToLimitWarning}
          isOverCharLimit={messageLimits.isOverCharLimit}
          isOverEncryptedLimit={messageLimits.isOverEncryptedLimit}
          mobileBubble={
            IS_MOBILE ? (
              <MobileFormattingBubble
                textareaRef={textareaRef}
                value={value}
                onChange={updateValue}
              />
            ) : undefined
          }
          actionButtons={
            <>
              <aside className="flex items-center gap-2">
                <div className="flex size-9 items-center justify-center rounded-full border border-border">
                  {IS_MOBILE ? (
                    <MobileAttachmentMenu />
                  ) : (
                    <DesktopAttachmentMenu />
                  )}
                </div>
              </aside>
              {IS_MOBILE ? (
                <div className="flex-1" />
              ) : (
                <div className="flex flex-1 items-center">
                  <MarkdownToolbar
                    textareaRef={textareaRef}
                    value={value}
                    onChange={updateValue}
                  />
                </div>
              )}
              <aside className="flex items-center gap-2">
                {!IS_MOBILE && (
                  <div className="flex size-9 items-center justify-center rounded-full border border-border">
                    <EmojiButton
                      onClick={() => textareaRef.current?.focus()}
                      insertText={insertText}
                    />
                  </div>
                )}
                {canSendMessage ? (
                  <div
                    className={cn(
                      "flex size-10 items-center justify-center rounded-full",
                      IS_MOBILE ? "bg-primary" : "bg-muted",
                    )}
                  >
                    <Button
                      disabled={sendDisabled}
                      size="icon"
                      variant="ghost"
                      aria-label={t("send")}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSendMessage}
                      className={cn(
                        "size-6 shrink-0 !bg-transparent lg:cursor-pointer",
                        IS_MOBILE
                          ? "text-primary-foreground hover:text-primary-foreground"
                          : "!hover:bg-transparent",
                      )}
                    >
                      {editMessage ? <CircleCheck /> : <SendHorizonal />}
                    </Button>
                  </div>
                ) : (
                  <RecordVoiceMessageButton
                    requestMic={requestMic}
                    startRecording={handleStartRecording}
                    voiceRecordingState={voiceRecordingStatus}
                    onGestureStart={handleGestureStart}
                    onImmediateSend={handleImmediateSend}
                    onImmediateLock={handleImmediateLock}
                    className={
                      IS_MOBILE
                        ? "bg-primary text-primary-foreground"
                        : undefined
                    }
                  />
                )}
              </aside>
            </>
          }
        />
      )}
    </div>
  );
}
